/**
 * Formatting + label/colour vocabulary shared by every admin screen,
 * so "Google Play", "Past due", "Studio+" etc. read the same everywhere.
 */
import type { Channel, Ms, PlatformKey } from './adminTypes';

export function fmtDate(ms: Ms): string {
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function fmtDateTime(ms: Ms): string {
  if (!ms) return '—';
  return new Date(ms).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function fmtRelative(ms: Ms, now = Date.now()): string {
  if (!ms) return 'Never';
  const diff = now - ms;
  const future = diff < 0;
  const abs = Math.abs(diff);
  const min = 60_000;
  const hour = 60 * min;
  const day = 24 * hour;
  let text: string;
  if (abs < min) text = 'just now';
  else if (abs < hour) text = `${Math.round(abs / min)} min`;
  else if (abs < day) text = `${Math.round(abs / hour)} h`;
  else if (abs < 30 * day) text = `${Math.round(abs / day)} d`;
  else if (abs < 365 * day) text = `${Math.round(abs / (30 * day))} mo`;
  else text = `${(abs / (365 * day)).toFixed(1)} y`;
  if (text === 'just now') return text;
  return future ? `in ${text}` : `${text} ago`;
}

export function fmtInr(n: number): string {
  return `₹${Math.round(n).toLocaleString('en-IN')}`;
}

export function fmtMoney(amount: number | null, currency: string | null): string {
  if (amount === null) return '—';
  if (!currency || currency === 'INR') return `₹${amount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
  return `${currency} ${amount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
}

export function fmtBytes(b: number | null): string {
  if (b === null) return 'Unlimited';
  if (!b) return '0 MB';
  if (b >= 1024 ** 3) return `${(b / 1024 ** 3).toFixed(b >= 10 * 1024 ** 3 ? 0 : 1)} GB`;
  if (b >= 1024 ** 2) return `${Math.round(b / 1024 ** 2)} MB`;
  return `${Math.max(1, Math.round(b / 1024))} KB`;
}

/** What each upload kind is, in studio language. */
export const STORAGE_KIND_LABEL: Record<string, string> = {
  dpr: 'Site photos (daily reports)',
  transaction: 'Receipts & payment proofs',
  design: 'Design files',
  laminate: 'Laminates',
  task_photo: 'Task photos',
  task_update: 'Task progress photos',
  project_cover: 'Project covers & studio branding',
  whiteboard_thumb: 'Whiteboards (retired)',
  studio_cover: 'Studio branding',
  studio_logo: 'Studio branding',
};

export function fmtPhone(p: string | null): string {
  if (!p) return '—';
  const m = /^\+91(\d{5})(\d{5})$/.exec(p);
  return m ? `+91 ${m[1]} ${m[2]}` : p;
}

export function waLink(p: string | null): string | null {
  if (!p) return null;
  return `https://wa.me/${p.replace(/[^\d]/g, '')}`;
}

// ── Vocabulary ─────────────────────────────────────────────────────

export const TIER_LABEL: Record<string, string> = {
  free: 'Free',
  solo: 'Solo',
  studio: 'Studio',
  studio_plus: 'Studio+',
  agency: 'Agency',
};
export const TIER_COLOR: Record<string, string> = {
  free: 'gray',
  solo: 'blue',
  studio: 'indigo',
  studio_plus: 'violet',
  agency: 'grape',
};

export const STATUS_LABEL: Record<string, string> = {
  active: 'Active',
  trialing: 'Trial',
  past_due: 'Past due',
  cancelled: 'Cancelled',
  expired: 'Expired',
};
export const STATUS_COLOR: Record<string, string> = {
  active: 'teal',
  trialing: 'cyan',
  past_due: 'orange',
  cancelled: 'gray',
  expired: 'red',
};

export const CHANNEL_LABEL: Record<Channel, string> = {
  app_store: 'App Store',
  play_store: 'Google Play',
  web: 'Web (Razorpay)',
  comp: 'Admin / comp',
  trial: 'Trial',
  free: 'Free',
};
export const CHANNEL_COLOR: Record<Channel, string> = {
  app_store: 'dark',
  play_store: 'green',
  web: 'blue',
  comp: 'grape',
  trial: 'cyan',
  free: 'gray',
};

export const PLATFORM_LABEL: Record<PlatformKey, string> = {
  ios: 'iOS',
  android: 'Android',
  web: 'Web',
  mobile: 'Mobile app',
  unknown: 'Not recorded',
};
export const PLATFORM_COLOR: Record<PlatformKey, string> = {
  ios: 'dark',
  android: 'green',
  web: 'blue',
  mobile: 'indigo',
  unknown: 'gray',
};

export const ROLE_LABEL: Record<string, string> = {
  superAdmin: 'Owner',
  admin: 'Admin',
  manager: 'Manager',
  accountant: 'Accountant',
  designer: 'Designer',
  siteEngineer: 'Site engineer',
  supervisor: 'Supervisor',
  viewer: 'Viewer',
  client: 'Client',
};

export const EVENT_LABEL: Record<string, string> = {
  INITIAL_PURCHASE: 'New subscription',
  RENEWAL: 'Renewal',
  PRODUCT_CHANGE: 'Plan change',
  CANCELLATION: 'Auto-renew turned off',
  UNCANCELLATION: 'Auto-renew turned on',
  EXPIRATION: 'Expired',
  BILLING_ISSUE: 'Payment failed',
  SUBSCRIPTION_PAUSED: 'Paused',
  TRANSFER: 'Transferred',
  NON_RENEWING_PURCHASE: 'One-time purchase',
  SUBSCRIBER_ALIAS: 'Account linked',
  TEST: 'Test event',
  WEB_PAYMENT: 'Web payment',
};

export const STORE_LABEL: Record<string, string> = {
  app_store: 'App Store',
  play_store: 'Google Play',
  razorpay: 'Web (Razorpay)',
};

export function label<T extends string>(map: Record<string, string>, key: T | null | undefined): string {
  if (!key) return '—';
  return map[key] ?? key.replace(/_/g, ' ');
}

// ── CSV export ─────────────────────────────────────────────────────

export function downloadCsv(filename: string, header: string[], rows: Array<Array<string | number | null>>) {
  const esc = (v: string | number | null) => {
    const s = v === null || v === undefined ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [header, ...rows].map((r) => r.map(esc).join(',')).join('\n');
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
